/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Crossword } from "@/models/Crossword";

// Constants
const PUZZLE_ITERATION_LIMIT = 1000000
const BLACK_SQUARE = "#"
const ACROSS = "ACROSS"
const DOWN = "DOWN"

// Helper constructs
let CLUE_BY_WORD_CACHE: Map<string, Clue> = new Map();
let ACROSS_STARTING_CLUES: Map<string, number> = new Map();
let DOWN_STARTING_CLUES: Map<string, number> = new Map();
let LENGTH_TO_CLUES: Map<number, Trie> = new Map();

// Helper classes
class Clue {
    id: string;
    solution: string;

    constructor(id: string, solution: string) {
        this.id = id;
        this.solution = solution;
    }
}

class CluePlacement {
    clue: Clue;
    x: number;
    y: number;
    direction: string;
    hint: string | null = null;
    clueNumber: number | null = null;

    constructor(clue: Clue, x: number, y: number, direction: string) {
        this.clue = clue;
        this.x = x;
        this.y = y;
        this.direction = direction;
    }
}

class TrieNode {
    children: Record<string, TrieNode>;
    isEndOfWord: boolean;

    constructor() {
        this.children = {};
        this.isEndOfWord = false;
    }
}

class Trie {
    root: TrieNode;

    constructor() {
        this.root = new TrieNode();
    }

    insert(word: string): void {
        let node = this.root;
        for (const char of word) {
            if (!(char in node.children)) {
                node.children[char] = new TrieNode();
            }
            node = node.children[char];
        }
        node.isEndOfWord = true;
    }

    search(word: string, node: TrieNode | null = null, index = 0, wildcard = '?', currentWord = '', matches: string[] = []): string[] {
        if (!node) {
            node = this.root;
        }

        if (index === word.length) {
            if (node.isEndOfWord) {
                matches.push(currentWord);
            }
            return matches;
        }

        const char = word[index];
        if (char === wildcard) {
            for (const [childChar, childNode] of Object.entries(node.children)) {
                this.search(word, childNode, index + 1, wildcard, currentWord + childChar, matches);
            }
        } else if (char in node.children) {
            this.search(word, node.children[char], index + 1, wildcard, currentWord + char, matches);
        }

        return matches;
    }

    searchRandom(word: string, wildcard = '?'): string[] {
        const matches = this.search(word, undefined, 0, wildcard);
        return matches.length ? this.getRandomSample(matches, 5) : [];
    }

    private getRandomSample<T>(arr: T[], size: number): T[] {
        return arr.sort(() => Math.random() - 0.5).slice(0, size);
    }
}


function hashBoard(grid: string[][]): string {
    /**
     * Hash the board for tracking
     *
     * @param grid The grid
     * @returns Board hash
     */
    const boardString = grid.map(row => row.join('')).join('');
    
    let hash = 5381;
    for (let i = 0; i < boardString.length; i++) {
        hash = ((hash << 5) + hash) + boardString.charCodeAt(i);
    }
    
    return (hash >>> 0).toString(16);
}

function checkIsHorizontalStartingSquare(grid: string[][], x: number, y: number): number | null {
    /**
     * Check if the square is a horizontal starting square
     *
     * @param grid The current grid state
     * @param x The row index
     * @param y The column index
     * @returns The length of the clue, if a starting square
     */
    // A black square is never a valid starting square
    if (grid[x][y] === BLACK_SQUARE) {
        return null;
    }
    // First column is always a starting square
    if (y === 0) {
        let length = 0;
        while (y < grid[x].length && grid[x][y] !== BLACK_SQUARE) {
            length++;
            y++;
        }
        // A starting square must be minimum 3
        if (length >= 3) {
            return length;
        }
    }
    // Starting square if the left square is black
    if (grid[x][y - 1] === BLACK_SQUARE) {
        let length = 0;
        while (y < grid[x].length && grid[x][y] !== BLACK_SQUARE) {
            length++;
            y++;
        }
        // A starting square must be minimum 3
        if (length >= 3) {
            return length;
        }
    }
    return null;
}

function checkIsDownStartingSquare(grid: string[][], x: number, y: number): number | null {
    /**
     * Check if the square is a down starting square
     *
     * @param grid The current grid state
     * @param x The row index
     * @param y The column index
     * @param BLACK_SQUARE The character representing a black square
     * @returns The length of the clue, if a starting square
     */
    if (grid[x][y] === BLACK_SQUARE) {
        return null;
    }
    if (x === 0) {
        let length = 0;
        while (x < grid.length && grid[x][y] !== BLACK_SQUARE) {
            length++;
            x++;
        }
        if (length >= 3) {
            return length;
        }
    }
    if (grid[x - 1][y] === BLACK_SQUARE) {
        let length = 0;
        while (x < grid.length && grid[x][y] !== BLACK_SQUARE) {
            length++;
            x++;
        }
        if (length >= 3) {
            return length;
        }
    }
    return null;
}

function isEmptyClue(clueStr: string): boolean {
    /**
     * Check if the clue is empty or not
     * 
     * @param clueStr Clue string
     * @returns Is clue empty or not
     */
    if (clueStr) {
        for (const character of clueStr) {
            if (character !== "?") {
                return false;
            }
        }
    }
    return true;
}

function getClueByWord(word: string): Clue | undefined {
    /**
     * Get clue by word
     *
     * @param word - The word to look up
     * @returns The corresponding Clue, if found
     */
    return CLUE_BY_WORD_CACHE.get(word);
}

function getStartingClueAtPosition(
    grid: string[][], 
    x: number, 
    y: number, 
    direction: string
): string {
    /**
     * Get the clue at the given position
     *
     * @param grid - The current grid
     * @param x - The row index
     * @param y - The column index
     * @param direction - The direction of the clue ("ACROSS" or "DOWN")
     * @returns The current clue as a string
     */
    const position = `${x},${y}`;

    if (direction === ACROSS) {
        const acrossDesiredLength = ACROSS_STARTING_CLUES.get(position)!;
        let desiredClue = "";
        for (let i = 0; i < acrossDesiredLength; i++) {
            desiredClue += grid[x][y + i] || "?";
        }
        return desiredClue;
    } else if (direction === DOWN) {
        const downDesiredLength = DOWN_STARTING_CLUES.get(position)!;
        let desiredClue = "";
        for (let i = 0; i < downDesiredLength; i++) {
            desiredClue += grid[x + i]?.[y] || "?";
        }
        return desiredClue;
    } else {
        throw new Error("Unknown direction");
    }
}

function checkPuzzleComplete(grid: string[][]): boolean {
    /**
     * Check if the puzzle is complete
     *
     * @param grid - The puzzle grid
     * @returns Whether the puzzle is complete (true) or not (false)
     */
    const rows = grid.length;
    const columns = grid[0].length;

    for (let x = 0; x < rows; x++) {
        for (let y = 0; y < columns; y++) {
            if (!grid[x][y]) {
                return false;
            }
        }
    }
    return true;
}

function checkPuzzleValid(grid: string[][]): boolean {
    /**
     * Check if the puzzle is valid, i.e., has a solution within
     * the given clue set
     *
     * @param grid - The current grid state
     * @returns Whether the puzzle is valid (true) or not (false)
     */
    const rows = grid.length;
    const columns = grid[0].length;

    for (let x = 0; x < rows; x++) {
        for (let y = 0; y < columns; y++) {
            const position = `${x},${y}`;
            let isXValid = false;
            let isYValid = false;

            // Check if the current position is the start of an across or down clue
            const isXStartingClue = ACROSS_STARTING_CLUES.get(position);
            const isYStartingClue = DOWN_STARTING_CLUES.get(position);

            // Check x fit
            if (isXStartingClue) {
                const desiredClue = getStartingClueAtPosition(grid, x, y, ACROSS);
                const validClueMatches = LENGTH_TO_CLUES.get(desiredClue.length)?.search(desiredClue);
                if (isEmptyClue(desiredClue)) {
                    isXValid = true;
                } else if (validClueMatches != undefined && validClueMatches.length > 0) {
                    isXValid = true;
                }
            }
            if (isXStartingClue && !isXValid) {
                return false;
            }

            // Check y fit
            if (isYStartingClue) {
                const desiredClue = getStartingClueAtPosition(grid, x, y, DOWN);
                const validClueMatches = LENGTH_TO_CLUES.get(desiredClue.length)?.search(desiredClue);
                if (isEmptyClue(desiredClue)) {
                    isYValid = true;
                } else if (validClueMatches != undefined && validClueMatches.length > 0) {
                    isYValid = true;
                }
            }
            if (isYStartingClue && !isYValid) {
                return false;
            }
        }
    }

    return true;
}

function getCluePlacements(grid: string[][]): CluePlacement[] {
    /**
     * Get the list of clue placements
     *
     * @param grid - The grid to place the clue on
     * @returns List of clues placed on the board
     */
    const rows = grid.length;
    const columns = grid[0].length;
    const results: CluePlacement[] = [];

    for (let x = 0; x < rows; x++) {
        for (let y = 0; y < columns; y++) {
            const position = `${x},${y}`;
            const isXStartingClue = position in ACROSS_STARTING_CLUES;
            const isYStartingClue = position in DOWN_STARTING_CLUES;

            if (isXStartingClue) {
                const startingClue = getStartingClueAtPosition(grid, x, y, ACROSS);
                if (!startingClue.includes("?")) {
                    const clue = getClueByWord(startingClue);
                    if (clue) {
                        results.push(new CluePlacement(clue, x, y, ACROSS));
                    }
                }
            }

            if (isYStartingClue) {
                const startingClue = getStartingClueAtPosition(grid, x, y, DOWN);
                if (!startingClue.includes("?")) {
                    const clue = getClueByWord(startingClue);
                    if (clue) {
                        results.push(new CluePlacement(clue, x, y, DOWN));
                    }
                }
            }
        }
    }

    return results;
}


function placeClue(clue: Clue, grid: string[][], x: number, y: number, direction: string): void {
    /**
     * Place the clue on the grid in the desired direction
     *
     * @param clue - The clue to be placed
     * @param grid - The grid to place the clue on
     * @param x - The row index to place the clue on
     * @param y - The column index to place the clue on
     * @param direction - The direction to place the clue (ACROSS or DOWN)
     */
    if (direction === ACROSS) {
        for (let characterIndex = 0; characterIndex < clue.solution.length; characterIndex++) {
            grid[x][y + characterIndex] = clue.solution[characterIndex];
        }
    } else if (direction === DOWN) {
        for (let characterIndex = 0; characterIndex < clue.solution.length; characterIndex++) {
            grid[x + characterIndex][y] = clue.solution[characterIndex];
        }
    } else {
        throw new Error("Unknown direction");
    }
}


export function autofillPuzzle(currentGrid: string[][], crossword: Crossword, wordList: string[]): string[][] | undefined {
    // Add block cells to current grid
    for (const blackCell of crossword.blackCells) {
        currentGrid[blackCell.x][blackCell.y] = "#"
    }

    // Create the clues
    let clues: Clue[] = wordList.map((word, index) => new Clue(`clue${index + 1}`, word));

    // Reset past constructs
    CLUE_BY_WORD_CACHE = new Map();
    ACROSS_STARTING_CLUES = new Map();
    DOWN_STARTING_CLUES = new Map();
    LENGTH_TO_CLUES = new Map();
    
    // Shuffle up
    clues.sort(() => Math.random() - 0.5);
    clues = clues.sort((a, b) => a.solution.length - b.solution.length);

    // Create by word cache
    for (const clue of clues) {
        CLUE_BY_WORD_CACHE.set(clue.solution, clue);
    }

    // Create an empty board
    const EMPTY_BOARD: string[][] = currentGrid;

    // Starting location [x, y] as key and clue length as value
    for (let x = 0; x < crossword.x; x++) {
        for (let y = 0; y < crossword.y; y++) {
            const horizontalLength = checkIsHorizontalStartingSquare(EMPTY_BOARD, x, y);
            if (horizontalLength) {
                ACROSS_STARTING_CLUES.set(`${x},${y}`, horizontalLength)
            }

            const verticalLength = checkIsDownStartingSquare(EMPTY_BOARD, x, y);
            if (verticalLength) {
                DOWN_STARTING_CLUES.set(`${x},${y}`, verticalLength)
            }
        }
    }

    // Organize clues by lengths into a trie
    for (const clue of clues) {
        if (!LENGTH_TO_CLUES.get(clue.solution.length)) {
            LENGTH_TO_CLUES.set(clue.solution.length, new Trie());
        }
        LENGTH_TO_CLUES.get(clue.solution.length)!.insert(clue.solution);
    }

    // Attempt to make a puzzle
    const validBoards: Array<string[][]> = [JSON.parse(JSON.stringify(EMPTY_BOARD))];
    const deadBoards: Set<string> = new Set();
    let iterations = 0;

    while (validBoards.length > 0 && iterations < PUZZLE_ITERATION_LIMIT) {
        iterations++;

        // Pop a valid scenario
        const grid = validBoards.pop()!;
        const rows = grid.length;
        const columns = grid[0].length;

        // Don't process dead boards
        const hashedBoard = hashBoard(grid);
        if (deadBoards.has(hashedBoard)) {
            continue;
        } else {
            deadBoards.add(hashedBoard);
        }

        // Get the clue Ids
        const pastPlacements = getCluePlacements(grid);
        const usedClueIds = pastPlacements.map(x => x.clue.id);


        // Check if puzzle is complete
        if (checkPuzzleComplete(grid)) {
            return grid;
        }

        // Create a copy of the board
        for (let x = 0; x < rows; x++) {
            for (let y = 0; y < columns; y++) {
                const position = `${x},${y}`
                const isXStartingClue = ACROSS_STARTING_CLUES.get(position);
                const isYStartingClue = DOWN_STARTING_CLUES.get(position);

                // Try to place an across clue
                if (isXStartingClue) {
                    const desiredClue = getStartingClueAtPosition(grid, x, y, ACROSS);
                    if (!desiredClue.includes('?')) continue;
                    const results = LENGTH_TO_CLUES.get(desiredClue.length)?.searchRandom(desiredClue) || [];
                    for (const result of results) {
                        const newGrid = JSON.parse(JSON.stringify(grid));
                        const clue = getClueByWord(result);
                        if (clue && !usedClueIds.includes(clue.id)) {
                            placeClue(clue, newGrid, x, y, ACROSS);
                            if (checkPuzzleValid(newGrid) && !deadBoards.has(hashBoard(newGrid))) {
                                validBoards.push(newGrid);
                            }
                        }
                    }
                }

                // Try to place a new down clue
                if (isYStartingClue) {
                    const desiredClue = getStartingClueAtPosition(grid, x, y, DOWN);
                    if (!desiredClue.includes('?')) continue;
                    const results = LENGTH_TO_CLUES.get(desiredClue.length)?.searchRandom(desiredClue) || [];
                    for (const result of results) {
                        const newGrid = JSON.parse(JSON.stringify(grid));
                        const clue = getClueByWord(result);
                        if (clue && !usedClueIds.includes(clue.id)) {
                            placeClue(clue, newGrid, x, y, DOWN);
                            if (checkPuzzleValid(newGrid) && !deadBoards.has(hashBoard(newGrid))) {
                                validBoards.push(newGrid);
                            }
                        }
                    }
                }
            }
        }
    }
}
